import { useId } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import {
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/hooks/use-theme";
import type { ThemePreference } from "@/lib/theme";

const OPTIONS: {
  value: ThemePreference;
  label: string;
  icon: typeof Sun;
}[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

/**
 * The "Appearance" row for the avatar menus: the word on the left, a segmented
 * control of three icon-only choices on the right. Renders no separators: each
 * menu places its own around the row. Choosing leaves the menu open so the
 * switch can be seen. See [[theme-menu-group.spec]] and [[theming]].
 */
export function ThemeMenuGroup() {
  const { preference, setPreference } = useTheme();
  const labelId = useId();

  return (
    <DropdownMenuRadioGroup
      aria-labelledby={labelId}
      value={preference}
      onValueChange={(value) => setPreference(value as ThemePreference)}
      className="flex items-center justify-between gap-3 px-3 py-2"
    >
      <span id={labelId} className="min-w-0 truncate text-[15px]">
        Appearance
      </span>
      <div className="flex shrink-0 rounded-[10px] bg-muted p-0.5">
        {OPTIONS.map(({ value, label, icon: Icon }) => (
          <DropdownMenuRadioItem
            key={value}
            value={value}
            aria-label={label}
            // The menu stays open: the page behind it is the preview.
            onSelect={(event) => event.preventDefault()}
            className="h-8 w-10 justify-center rounded-lg p-0 text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-popover data-[state=checked]:text-foreground data-[state=checked]:shadow-sm data-[state=checked]:focus:bg-popover data-[state=checked]:focus:text-foreground [&>span:first-child]:hidden"
          >
            <Icon aria-hidden="true" className="size-[17px]" />
          </DropdownMenuRadioItem>
        ))}
      </div>
    </DropdownMenuRadioGroup>
  );
}
